import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import { Offcanvas } from 'react-bootstrap';
import {
  FaBell,
  FaInfoCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaTrash,
  FaBellSlash,
  FaCog
} from 'react-icons/fa';
import MiniLoadingSpinner from 'components/Spinners/MiniLoadingSpinner';
import './NotificationCenter.scss';
import { toast } from 'react-toastify';
import {
  getAllUserNotificationsByUserId,
  markNotificationAsRead,
  deleteAllUserNotifications
} from '../../../../../../../../crud/users_api/user_notifications';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import MainHandler from './components/MainHandler';
import { useNotifications } from './NotificationContext';
import { useNavigate } from 'react-router-dom';
import { X } from 'react-bootstrap-icons';
import { Button } from 'react-bootstrap';
import { NotificationConfig } from './components/NotificationConfig';
import { notificationSoundManager } from '../../../../../../../../utils/notificationSound';
 
export interface Notification {
  notification_id: string;
  timestamp: string;
  local_id: number;
  user_id: number;
  message: string;
  context: string;  // "info" | "warning" | "error"
  topic: string;
  streamed: boolean;
  read: boolean;
  deleted: boolean;
  data: { content: string; [key: string]: any } | null;
}
 
const SOCKET_URL = "wss://vsagsavoa4.execute-api.us-east-2.amazonaws.com/production/";
 
const NotificationCenter: React.FC = () => {
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [showOffcanvas, setShowOffcanvas] = useState(false);
  const [currentView, setCurrentView] = useState<'notifications' | 'config'>('notifications');
  const [selectedTopic, setSelectedTopic] = useState('todo');
  const [connectionQuality, setConnectionQuality] = useState<number>(0);
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);
  const { setLastNotification } = useNotifications();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [notificationsLoaded, setNotificationsLoaded] = useState(false);

  // ── LOAD NOTIFICATIONS ──────────────────────────────────────────────────────
  const loadNotifications = async () => {
    if (notificationsLoaded) return; // Don't reload if already loaded
    
    setIsLoadingNotifications(true);
    try {
      const user = localStorage.getItem('user');
      const user_id = user ? JSON.parse(user).user_id : 0;
      
      const resp: any = await getAllUserNotificationsByUserId(user_id);
      const data = resp.data as Notification[];
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.read).length);
      setNotificationsLoaded(true);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Error cargando notificaciones');
    } finally {
      setIsLoadingNotifications(false);
    }
  };

  // ── HANDLE OFFCANVAS SHOW ───────────────────────────────────────────────────
  const handleShowOffcanvas = () => {
    setShowOffcanvas(true);
    loadNotifications(); // Load notifications only when offcanvas is shown
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
    };
    window.addEventListener('resize', handleResize);
    handleResize(); // Call on mount
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── ICON HELPERS ──────────────────────────────────────────────────────────────
  const getContextIcon = (ctx: string) => {
    switch (ctx) {
      case 'warning': return <FaExclamationTriangle className="notification-icon warning" />;
      case 'error':   return <FaTimesCircle       className="notification-icon error" />;
      default:        return <FaInfoCircle        className="notification-icon info" />;
    }
  };
 
  const formatTimestamp = (ts: string) => {
    const d = new Date(ts), now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
    }
    return d.toLocaleDateString([], {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };
 
  // ── ADD & REMOVE ─────────────────────────────────────────────────────────────
  const addNotification = async (n: Notification) => {
    // Check if this topic is enabled before processing
    const isEnabled = await notificationSoundManager.isTopicEnabled(n.topic);
    if (!isEnabled) {
      return; // Skip notifications for disabled topics
    }

    // Normalizar data si viene como string
    let data = n.data;
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch {
        data = null;
      }
    }
    const normalized = { ...n, data };
    setNotifications(prev => [normalized, ...prev]);
    setUnreadCount(c => c + 1);
    
    // Play notification sound based on topic settings
    notificationSoundManager.playNotificationSound(n.topic);
    
    // toast only for actual notifications
    if (n.context === 'error') toast.error(n.message);
    else if (n.context === 'warning') toast.warn(n.message);
    else toast.info(n.message);
  };
 
  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.notification_id !== id));
    setUnreadCount(c => Math.max(0, c - 1));
  };
 
  // ── WEBSOCKET + HEARTBEAT ────────────────────────────────────────────────────
  useEffect(() => {
    let socket: WebSocket;
    let reconnectTimer: number;
    let heartbeatTimer: number;
    let lastPingTs = 0;
    let isUnmounted = false;
 
    const startHeartbeat = () => {
      heartbeatTimer = window.setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          lastPingTs = Date.now();
          socket.send(JSON.stringify({ action: 'ping', timestamp: lastPingTs }));
        }
      }, 10_000); // every 10s
    };
 
    const stopHeartbeat = () => {
      clearInterval(heartbeatTimer);
    };
 
    const connectWebSocket = () => {
      socket = new WebSocket(SOCKET_URL);
      setWs(socket);
 
      socket.onopen = () => {
        //console.log('WS open');
        setConnectionQuality(5);
        startHeartbeat();
 
        const user = localStorage.getItem('user');
        const user_id = user ? JSON.parse(user).user_id : 0;
        socket.send(JSON.stringify({
          action: 'handler',
          data: { user_id, type: 'saveSocket' }
        }));
        
        // Remove automatic loading of notifications - will be loaded when offcanvas opens
      };
 
      socket.onmessage = evt => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.action === 'pong' && msg.timestamp === lastPingTs) {
            const rtt = Date.now() - msg.timestamp;
            // map RTT → bars
            let q = 0;
            if (rtt < 100)      q = 5;
            else if (rtt < 200) q = 4;
            else if (rtt < 300) q = 3;
            else if (rtt < 500) q = 2;
            else if (rtt < 1000) q = 1;
            setConnectionQuality(q);
          }
          else if (msg.notification_id && msg.message) {
            addNotification(msg);
            setCurrentNotification(msg);
            setLastNotification(msg); // <-- Actualiza el contexto global
          }
        } catch (err) {
          console.error('WS parse error', err);
        }
      };
 
      socket.onerror = () => {
        console.warn('WS error');
        // quietly degrade
        setConnectionQuality(q => Math.max(0, q - 1));
      };
 
      socket.onclose = () => {
        //console.log('WS closed');
        stopHeartbeat();
        setConnectionQuality(0);
        if (!isUnmounted) {
          reconnectTimer = window.setTimeout(connectWebSocket, 1_000);
        }
      };
    };
 
    connectWebSocket();
    return () => {
      isUnmounted = true;
      clearTimeout(reconnectTimer);
      stopHeartbeat();
      if (socket && (socket.readyState === 0 || socket.readyState === 1)) {
        socket.close();
      }
    };
  }, [setLastNotification]);
 
  // ── DELETE ALL NOTIFICATIONS ────────────────────────────────────────────────
  const handleDeleteAll = async () => {
    try {
      const user = localStorage.getItem('user');
      const user_id = user ? JSON.parse(user).user_id : 0;
      
      await deleteAllUserNotifications(user_id);
      setNotifications([]);
      setUnreadCount(0);
      toast.success('Todas las notificaciones han sido eliminadas');
    } catch (error) {
      console.error('Error deleting all notifications:', error);
      toast.error('Error eliminando todas las notificaciones');
    }
  };

  // ── MARK AS READ ─────────────────────────────────────────────────────────────
  const markAsRead = (id: string) => {
    markNotificationAsRead(id)
      .then(() => removeNotification(id))
      .catch(err => {
        console.error(err);
        toast.error('Error eliminando la notificación');
      });
  };
 
  // ── TOPICS & FILTER ──────────────────────────────────────────────────────────
  const uniqueTopics = useMemo(() => {
    const s = new Set<string>();
    notifications.forEach(n => n.topic && s.add(n.topic));
    return ['todo', ...Array.from(s)];
  }, [notifications]);
 
  const filtered = useMemo(() => {
    return selectedTopic === 'todo'
      ? notifications
      : notifications.filter(n => n.topic === selectedTopic);
  }, [notifications, selectedTopic]);
 
  // ── RENDER ───────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="notification-center">
        <button className="notification-bell" onClick={handleShowOffcanvas}>
          <FaBell size={20} />
          {unreadCount > 0 && <span className="notification-count">{unreadCount}</span>}
        </button>
            <OverlayTrigger
            placement="bottom"
            overlay={
              <Tooltip id="connection-quality-tooltip">
              Ancho de banda: {connectionQuality}/5
              </Tooltip>
            }
            >
            <div className="signal-indicator">
              {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`bar ${connectionQuality > i ? 'active' : ''}`} />
              ))}
            </div>
            </OverlayTrigger>
      </div>
 
      <Offcanvas
        show={showOffcanvas}
        onHide={() => setShowOffcanvas(false)}
        placement="end"
        className="mp-modal-slider notification-offcanvas"
      >
        <Offcanvas.Header className="notification-header">
          <div className="header-content">
            <Offcanvas.Title className="header-title">Notificaciones</Offcanvas.Title>
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </div>
          <div className="header-actions">
            <Button 
              variant="none" 
              className="config-btn"
              onClick={() => setCurrentView('config')}
              title="Configurar notificaciones"
            >
              <FaCog size={16} />
            </Button>
            {notifications.length > 0 && !isLoadingNotifications && (
              <Button 
                variant="none" 
                className="delete-all-btn"
                onClick={handleDeleteAll}
                title="Eliminar todas las notificaciones"
              >
                <FaTrash size={14} />
                <span>Eliminar todo</span>
              </Button>
            )}
            {isMobile && (
              <Button variant="none" className="close-btn" onClick={() => setShowOffcanvas(false)}>
                <X size={20} />
              </Button>
            )}
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {currentView === 'config' ? (
            <NotificationConfig onBack={() => setCurrentView('notifications')} />
          ) : isLoadingNotifications ? (
            <div className="loading-container">
              <MiniLoadingSpinner />
              <p>Cargando notificaciones...</p>
            </div>
          ) : (
            <>
              <div className="topics-container">
                {uniqueTopics.map(topic => (
                  <div
                    key={topic}
                    className={`topic-item ${selectedTopic === topic ? 'selected' : ''}`}
                    onClick={() => setSelectedTopic(topic)}
                  >
                    {topic.toUpperCase()}
                  </div>
                ))}
              </div>
 
              {filtered.length === 0 ? (
                <div className="empty-notifications">
                  <FaBellSlash className="empty-icon" />
                  <div className="empty-title">Sin notificaciones</div>
                  <div className="empty-message">
                    No hay notificaciones en este momento. Te mantendremos informado cuando lleguen nuevas actualizaciones.
                  </div>
                </div>
              ) : (
                filtered.map(n => (
                  <div
                    key={n.notification_id}
                    className="notification-item"
                    data-context={n.context}
                  >
                    <div className="notification-item-header">
                      {getContextIcon(n.context)}
                      <span className="notification-topic-pill">{n.topic.toUpperCase()}</span>
                      <small className="notification-timestamp">
                        {formatTimestamp(n.timestamp)}
                      </small>
                    </div>
                    <div className="notification-item-body">
                      <div className='info-notification'>
                        <p className="notification-message">{n.message}</p>
                        <button
                          className="notification-trash"
                          onClick={e => {
                            e.stopPropagation();
                            markAsRead(n.notification_id);
                          }}
                        >
                          <FaTrash />
                        </button>
                      </div>
                      {n.data?.path && (
                        <button className='notification-link' onClick={e => {
                          e.stopPropagation();
                          navigate(n.data?.path);
                        }}>
                          Ir
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </>
          )}
        </Offcanvas.Body>
      </Offcanvas>
      
      <MainHandler
        notification={currentNotification}
        setNotification={setCurrentNotification}
      />
    </>
  );
};
 
export default NotificationCenter;